import * as M from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { ChangeEvent, useEffect } from 'react';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';
import {
  activationStatusOptions,
  modelStatusOptions,
  modemTypeOptions,
  telecomPlanOptionsByProvider,
  telecomProviderOptions,
} from '../../../../../../shared/constants/options';
import type { IModemRegisterFormValues } from '../../../../../../shared/types/modem';

interface IModemInfoFormProps {
  register: UseFormRegister<IModemRegisterFormValues>;
  errors: FieldErrors<IModemRegisterFormValues>;
  control: Control<IModemRegisterFormValues>;
  setValue: UseFormSetValue<IModemRegisterFormValues>;
  isEdit: boolean;
  initialModemImei: string;
}

export default function ModemInfoForm(props: IModemInfoFormProps) {
  const { control, register, errors, setValue, isEdit, initialModemImei } = props;

  const modem = useWatch({
    control,
    name: 'modem',
  });
  const activateStatus = useWatch({
    control,
    name: 'activateStatus',
  });
  const serviceNumber1 = useWatch({
    control,
    name: 'serviceNumber1',
  });
  const serviceNumber2 = useWatch({
    control,
    name: 'serviceNumber2',
  });
  const serviceNumber3 = useWatch({
    control,
    name: 'serviceNumber3',
  });
  const telecom = useWatch({
    control,
    name: 'telecom',
  });

  const isModemSerialNumberRequired = modem !== 'NONE';
  const isCanceledStatus = activateStatus === 'CANCELED';
  const canInputStartDate = activateStatus === 'COMPLETED' || isCanceledStatus;
  const canInputEndDate = isCanceledStatus;
  const isModemImeiDisabled = isEdit && !!initialModemImei;
  const serviceNumberError = errors.serviceNumber1?.message || errors.serviceNumber2?.message || errors.serviceNumber3?.message;
  const { ref: serviceNumber1Ref, name: serviceNumber1Name, onBlur: handleServiceNumber1Blur } = register('serviceNumber1');
  const { ref: serviceNumber2Ref, name: serviceNumber2Name, onBlur: handleServiceNumber2Blur } = register('serviceNumber2');
  const { ref: serviceNumber3Ref, name: serviceNumber3Name, onBlur: handleServiceNumber3Blur } = register('serviceNumber3');

  useEffect(() => {
    if (!canInputStartDate) {
      setValue('activateStartDt', null, { shouldDirty: true, shouldValidate: true });
      setValue('activateEndDt', null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (!canInputEndDate) {
      setValue('activateEndDt', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [canInputEndDate, canInputStartDate, setValue]);

  const handleServiceNumberChange = (
    key: 'serviceNumber1' | 'serviceNumber2' | 'serviceNumber3',
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue = event.currentTarget.value.replace(/[^0-9]/g, '');
    const serviceNumberMap = {
      serviceNumber1,
      serviceNumber2,
      serviceNumber3,
      [key]: nextValue,
    };
    const serviceNumberParts = [
      serviceNumberMap.serviceNumber1,
      serviceNumberMap.serviceNumber2,
      serviceNumberMap.serviceNumber3,
    ].filter((value) => !!value);
    setValue(key, nextValue, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('serviceNo', serviceNumberParts.join('-'), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <>
      <M.Fieldset
        styles={(theme) => ({
          legend: {
            fontSize: theme.fontSizes.lg,
            fontWeight: 600,
            marginBottom: 0,
          },
        })}
        legend="모뎀 상세 입력"
        variant="unstyled"
      >
        <M.Grid>
          <M.Grid.Col span={3}>
            <Controller
              name="modem"
              control={control}
              render={({ field, fieldState }) => (
                <M.Select
                  {...field}
                  withAsterisk
                  disabled={isEdit}
                  label="모뎀/라우터"
                  placeholder="모뎀/라우터를 선택하세요."
                  data={modemTypeOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <M.TextInput
              withAsterisk={isModemSerialNumberRequired}
              label="단말기 일련번호"
              disabled={isEdit}
              placeholder="단말기 일련번호를 입력하세요."
              error={errors.modemNo?.message}
              {...register('modemNo')}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <M.TextInput
              label="단말기 IMEI"
              disabled={isModemImeiDisabled}
              placeholder="단말기 IMEI를 입력하세요."
              error={errors.modemImei?.message}
              {...register('modemImei')}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <Controller
              name="statusYn"
              control={control}
              render={({ field, fieldState }) => (
                <M.Select
                  {...field}
                  withAsterisk
                  label="모뎀 상태"
                  placeholder="모뎀 상태를 선택하세요."
                  data={modelStatusOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
          </M.Grid.Col>
        </M.Grid>
      </M.Fieldset>
      <M.Divider my="lg" />
      <M.Fieldset
        styles={(theme) => ({
          legend: {
            fontSize: theme.fontSizes.lg,
            fontWeight: 600,
            marginBottom: 0,
          },
        })}
        legend="통신 서비스 및 가입 정보"
        variant="unstyled"
      >
        <M.Grid>
          <M.Grid.Col span={3}>
            <Controller
              name="telecom"
              control={control}
              render={({ field, fieldState }) => (
                <M.Select
                  {...field}
                  label="통신사"
                  placeholder="통신사를 선택하세요."
                  data={telecomProviderOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <Controller
              name="plan"
              control={control}
              render={({ field, fieldState }) => (
                <M.Select
                  {...field}
                  label="통신 요금제"
                  placeholder="통신 요금제를 선택하세요."
                  data={telecom ? telecomPlanOptionsByProvider[telecom] : []}
                  error={fieldState.error?.message}
                />
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <M.TextInput
              label="USIM 일련번호"
              placeholder="USIM 일련번호를 입력하세요."
              error={errors.usimNo?.message}
              {...register('usimNo')}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <M.Input.Wrapper label="서비스 번호" error={serviceNumberError}>
              <M.Group gap="xs" wrap="nowrap" align="flex-end">
                <M.TextInput
                  placeholder="000"
                  maxLength={3}
                  error={!!serviceNumberError}
                  name={serviceNumber1Name}
                  ref={serviceNumber1Ref}
                  onBlur={handleServiceNumber1Blur}
                  value={serviceNumber1 ?? ''}
                  onChange={(event) => handleServiceNumberChange('serviceNumber1', event)}
                />
                <M.Text span mb={8}>
                  -
                </M.Text>
                <M.TextInput
                  placeholder="0000"
                  maxLength={4}
                  error={!!serviceNumberError}
                  name={serviceNumber2Name}
                  ref={serviceNumber2Ref}
                  onBlur={handleServiceNumber2Blur}
                  value={serviceNumber2 ?? ''}
                  onChange={(event) => handleServiceNumberChange('serviceNumber2', event)}
                />
                <M.Text span mb={8}>
                  -
                </M.Text>
                <M.TextInput
                  placeholder="0000"
                  maxLength={4}
                  error={!!serviceNumberError}
                  name={serviceNumber3Name}
                  ref={serviceNumber3Ref}
                  onBlur={handleServiceNumber3Blur}
                  value={serviceNumber3 ?? ''}
                  onChange={(event) => handleServiceNumberChange('serviceNumber3', event)}
                />
              </M.Group>
            </M.Input.Wrapper>
          </M.Grid.Col>
        </M.Grid>
      </M.Fieldset>
      <M.Divider my="lg" />
      <M.Fieldset
        styles={(theme) => ({
          legend: {
            fontSize: theme.fontSizes.lg,
            fontWeight: 600,
            marginBottom: 0,
          },
        })}
        legend="개통 정보"
        variant="unstyled"
      >
        <M.Grid>
          <M.Grid.Col span={3}>
            <Controller
              name="activateStatus"
              control={control}
              render={({ field, fieldState }) => (
                <M.Select
                  {...field}
                  label="개통 진행상태"
                  placeholder="개통 진행상태를 선택하세요."
                  data={activationStatusOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <Controller
              name="contractPeriod"
              control={control}
              render={({ field }) => (
                <M.Radio.Group {...field} label="약정 여부">
                  <M.Group mt={8}>
                    <M.Radio value="36" label="3년 약정" />
                    <M.Radio value="24" label="2년 약정" />
                    <M.Radio value="NONE" label="약정 없음" />
                  </M.Group>
                </M.Radio.Group>
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <Controller
              name="activateStartDt"
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  {...field}
                  flex={1}
                  value={field.value}
                  label="개통일"
                  valueFormat="YYYY-MM-DD"
                  placeholder="YYYY-MM-DD"
                  disabled={!canInputStartDate}
                  error={errors.activateStartDt?.message}
                />
              )}
            />
          </M.Grid.Col>
          <M.Grid.Col span={3}>
            <Controller
              name="activateEndDt"
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  {...field}
                  flex={1}
                  value={field.value}
                  label="개통 해지일"
                  valueFormat="YYYY-MM-DD"
                  placeholder="YYYY-MM-DD"
                  disabled={!canInputEndDate}
                  error={errors.activateEndDt?.message}
                />
              )}
            />
          </M.Grid.Col>
        </M.Grid>
      </M.Fieldset>
      <M.Divider my="lg" />
      <M.Grid>
        <M.Grid.Col span={6}>
          <M.TextInput label="비고" placeholder="비고를 입력하세요." error={errors.memo?.message} {...register('memo')} />
        </M.Grid.Col>
      </M.Grid>
    </>
  );
}
